import { useState, useEffect } from "react";
import { Layout } from "@/components/Layout";
import { API } from "@/App";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Trophy, Medal, Crown, TrendingUp, Users } from "lucide-react";
import axios from "axios";

export const LeaderboardPage = () => {
  const [leaderboard, setLeaderboard] = useState({ top_balances: [], top_winners: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLeaderboard = async () => {
      try {
        const response = await axios.get(`${API}/leaderboard`);
        setLeaderboard(response.data);
      } catch (error) {
        console.error("Leaderboard fetch error:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchLeaderboard();
  }, []);

  const getRankIcon = (rank) => {
    if (rank === 0) return <Crown className="w-5 h-5 text-[#FFD700]" />;
    if (rank === 1) return <Medal className="w-5 h-5 text-[#C0C0C0]" />;
    if (rank === 2) return <Medal className="w-5 h-5 text-[#CD7F32]" />;
    return <span className="text-[#52525B] font-bold">{rank + 1}</span>;
  };

  const getRankStyle = (rank) => {
    if (rank === 0) return 'bg-gradient-to-r from-[#FFD700]/20 to-transparent border-[#FFD700]/30';
    if (rank === 1) return 'bg-gradient-to-r from-[#C0C0C0]/20 to-transparent border-[#C0C0C0]/30';
    if (rank === 2) return 'bg-gradient-to-r from-[#CD7F32]/20 to-transparent border-[#CD7F32]/30';
    return 'bg-white/5 border-transparent';
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-xl bg-[#FFD600]/20 flex items-center justify-center">
            <Trophy className="w-6 h-6 text-[#FFD600]" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white font-['Rajdhani']" data-testid="leaderboard-title">
              Leaderboard
            </h1>
            <p className="text-[#A1A1AA] text-sm">Top players on BetIndia</p>
          </div>
        </div>

        <Tabs defaultValue="balance" className="space-y-6">
          <TabsList className="bg-[#1A1B1F] border border-white/[0.08]">
            <TabsTrigger value="balance" className="data-[state=active]:bg-[#FFD600] data-[state=active]:text-black">
              <TrendingUp className="w-4 h-4 mr-2" /> Top Balances
            </TabsTrigger>
            <TabsTrigger value="winners" className="data-[state=active]:bg-[#FFD600] data-[state=active]:text-black">
              <Trophy className="w-4 h-4 mr-2" /> Top Winners
            </TabsTrigger>
          </TabsList>

          {/* Top Balances */}
          <TabsContent value="balance">
            {loading ? (
              <div className="text-center py-12">
                <div className="w-12 h-12 border-4 border-[#FFD600] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                <p className="text-[#A1A1AA]">Loading leaderboard...</p>
              </div>
            ) : leaderboard.top_balances.length === 0 ? (
              <div className="text-center py-12">
                <Users className="w-12 h-12 text-[#52525B] mx-auto mb-4" />
                <p className="text-[#A1A1AA]">No players yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {leaderboard.top_balances.map((player, idx) => (
                  <div
                    key={idx}
                    className={`flex items-center gap-4 p-4 rounded-xl border ${getRankStyle(idx)} transition-all hover:scale-[1.01]`}
                    data-testid={`leaderboard-row-${idx}`}
                  >
                    {/* Rank */}
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      idx === 0 ? 'bg-[#FFD700]' : 
                      idx === 1 ? 'bg-[#C0C0C0]' : 
                      idx === 2 ? 'bg-[#CD7F32]' : 'bg-white/10'
                    }`}>
                      {idx < 3 ? (
                        <span className="text-black font-bold">{idx + 1}</span>
                      ) : (
                        <span className="text-white font-bold">{idx + 1}</span>
                      )}
                    </div>
                    
                    {/* Player Info */}
                    <div className="flex-1">
                      <p className={`font-bold ${idx < 3 ? 'text-white text-lg' : 'text-white'}`}>
                        {player.name}
                      </p>
                      <p className="text-[#52525B] text-sm">{player.email}</p>
                    </div>
                    
                    {/* Balance */}
                    <div className="text-right">
                      <p className={`font-mono font-bold ${
                        idx === 0 ? 'text-[#FFD700] text-xl' : 
                        idx < 3 ? 'text-[#00E701] text-lg' : 'text-[#00E701]'
                      }`}>
                        ₹{(player.balance || 0).toLocaleString('en-IN')}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Top Winners */}
          <TabsContent value="winners">
            {loading ? (
              <div className="text-center py-12">
                <div className="w-12 h-12 border-4 border-[#FFD600] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                <p className="text-[#A1A1AA]">Loading leaderboard...</p>
              </div>
            ) : leaderboard.top_winners.length === 0 ? (
              <div className="text-center py-12">
                <Trophy className="w-12 h-12 text-[#52525B] mx-auto mb-4" />
                <p className="text-[#A1A1AA]">No winners yet - start playing!</p>
              </div>
            ) : (
              <div className="space-y-3">
                {leaderboard.top_winners.map((winner, idx) => (
                  <div
                    key={idx}
                    className={`flex items-center gap-4 p-4 rounded-xl border ${getRankStyle(idx)} transition-all hover:scale-[1.01]`}
                  >
                    {/* Rank */}
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      idx === 0 ? 'bg-[#FFD700]' : 
                      idx === 1 ? 'bg-[#C0C0C0]' : 
                      idx === 2 ? 'bg-[#CD7F32]' : 'bg-white/10'
                    }`}>
                      {idx < 3 ? (
                        <span className="text-black font-bold">{idx + 1}</span>
                      ) : (
                        <span className="text-white font-bold">{idx + 1}</span>
                      )}
                    </div>
                    
                    {/* Player Info */}
                    <div className="flex-1">
                      <p className="font-bold text-white">{winner._id}</p>
                    </div>
                    
                    {/* Winnings */}
                    <div className="text-right">
                      <p className="text-[#52525B] text-xs mb-1">Total Winnings</p>
                      <p className={`font-mono font-bold ${
                        idx === 0 ? 'text-[#FFD700] text-xl' : 
                        idx < 3 ? 'text-[#00E701] text-lg' : 'text-[#00E701]'
                      }`}>
                        ₹{(winner.total_winnings || 0).toLocaleString('en-IN')}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Info */}
        <div className="mt-8 p-4 rounded-lg bg-white/5 text-center">
          <p className="text-[#52525B] text-sm">
            Leaderboard updates in real-time. Keep playing to climb the ranks!
          </p>
        </div>
      </div>
    </Layout>
  );
};
